import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiSave,
  FiSend,
} from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import companySmtpService from "../../../services/companySmtp.service";
import { getApiErrorMessage } from "../../../utils/downloadExcel";
import "../../../styles/emailConfiguration.css";

const emptyForm = {
  fromEmail: "",
  host: "smtp.gmail.com",
  port: "587",
  username: "",
  password: "",
  secureMode: "STARTTLS",
  enabled: true,
};

const SECURITY_OPTIONS = [
  { value: "STARTTLS", title: "STARTTLS", hint: "Port 587" },
  { value: "TLS", title: "SSL / TLS", hint: "Port 465" },
  { value: "NONE", title: "None", hint: "Not recommended" },
];

const PRESETS = [
  { id: "gmail", label: "Gmail", host: "smtp.gmail.com", port: "587", secureMode: "STARTTLS" },
  { id: "outlook", label: "Outlook", host: "smtp.office365.com", port: "587", secureMode: "STARTTLS" },
];

function statusMeta({ loading, hasPassword, enabled, fromEmail }) {
  if (loading) return { className: "email-config-status--empty", label: "Loading" };
  if (enabled && hasPassword && fromEmail) {
    return { className: "email-config-status--on", label: "SMTP enabled" };
  }
  if (hasPassword && !enabled) {
    return { className: "email-config-status--off", label: "Saved · disabled" };
  }
  return { className: "email-config-status--empty", label: "Not configured" };
}

export default function EmailConfiguration() {
  const { auth } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [hasPassword, setHasPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!auth?.token) return;
    try {
      setLoading(true);
      setError("");
      const res = await companySmtpService.getConfig(auth.token);
      const config = res?.data?.config || {};
      setForm({
        fromEmail: config.fromEmail || "",
        host: config.host || "smtp.gmail.com",
        port: String(config.port || 587),
        username: config.username || "",
        password: "",
        secureMode: config.secureMode || "STARTTLS",
        enabled: config.enabled !== false,
      });
      setHasPassword(Boolean(config.hasPassword));
      setTestTo((prev) => prev || config.fromEmail || "");
    } catch (err) {
      setError(await getApiErrorMessage(err, "Could not load email configuration."));
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const applyPreset = (preset) => {
    setForm((prev) => ({
      ...prev,
      host: preset.host,
      port: preset.port,
      secureMode: preset.secureMode,
    }));
  };

  const setSecureMode = (mode) => {
    setForm((prev) => ({
      ...prev,
      secureMode: mode,
      port: mode === "TLS" ? "465" : mode === "STARTTLS" ? "587" : prev.port,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setSaving(true);
    try {
      const payload = {
        fromEmail: form.fromEmail.trim(),
        host: form.host.trim(),
        port: Number(form.port),
        username: form.username.trim() || form.fromEmail.trim(),
        secureMode: form.secureMode,
        enabled: form.enabled,
      };
      if (form.password.trim()) payload.password = form.password.trim();
      const res = await companySmtpService.saveConfig(payload, auth.token);
      const config = res?.data?.config || {};
      setHasPassword(Boolean(config.hasPassword));
      setForm((prev) => ({ ...prev, password: "" }));
      setMessage(res?.message || "SMTP configuration saved.");
    } catch (err) {
      setError(await getApiErrorMessage(err, "Could not save SMTP configuration."));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setError("");
    setMessage("");
    setTesting(true);
    try {
      const res = await companySmtpService.testConfig(testTo.trim(), auth.token);
      setMessage(res?.message || `Test email sent to ${testTo}.`);
    } catch (err) {
      setError(
        await getApiErrorMessage(
          err,
          "SMTP test failed. Check host, port, username, and app password."
        )
      );
    } finally {
      setTesting(false);
    }
  };

  const status = useMemo(
    () =>
      statusMeta({
        loading,
        hasPassword,
        enabled: form.enabled,
        fromEmail: form.fromEmail,
      }),
    [loading, hasPassword, form.enabled, form.fromEmail]
  );

  const canTest = hasPassword && form.enabled && !saving && !testing && !loading;

  return (
    <div className="content email-config-page">
      <header className="email-config-hero">
        <div className="email-config-hero__intro">
          <div className="email-config-hero__icon" aria-hidden>
            <FiMail />
          </div>
          <div>
            <h1>Email Configuration</h1>
            <p>
              Banker Repo Intimation emails and inventory photos are sent from this SMTP account.
              The password is encrypted and never shown to staff.
            </p>
          </div>
        </div>
        <span className={`email-config-status ${status.className}`}>
          <span className="email-config-status__dot" aria-hidden />
          {status.label}
        </span>
      </header>

      {error ? (
        <p className="email-config-alert email-config-alert--error" role="alert">
          <FiAlertCircle aria-hidden />
          {error}
        </p>
      ) : null}
      {message ? (
        <p className="email-config-alert email-config-alert--ok" role="status">
          <FiCheckCircle aria-hidden />
          {message}
        </p>
      ) : null}

      {loading ? (
        <div className="email-config-card">
          <div className="email-config-skeleton" aria-busy="true">
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : (
        <div className="email-config-layout">
          <form className="email-config-card" onSubmit={handleSave}>
            <div className="email-config-card__head">
              <div>
                <h2>SMTP settings</h2>
                <p>Use your agency mailbox. Gmail needs an App Password, not the normal login password.</p>
              </div>
              <div className="email-config-presets">
                {PRESETS.map((preset) => {
                  const active = form.host === preset.host && form.secureMode === preset.secureMode;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      className={`email-config-chip ${active ? "is-active" : ""}`}
                      onClick={() => applyPreset(preset)}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="email-config-card__body">
              <div className="email-config-grid">
                <div className="email-config-field email-config-field--full">
                  <label htmlFor="fromEmail">Email address</label>
                  <input
                    id="fromEmail"
                    name="fromEmail"
                    type="email"
                    value={form.fromEmail}
                    onChange={onChange}
                    onBlur={() => {
                      setForm((prev) => ({
                        ...prev,
                        username: prev.username || prev.fromEmail.trim(),
                      }));
                      if (!testTo && form.fromEmail) setTestTo(form.fromEmail.trim());
                    }}
                    required
                    autoComplete="off"
                    placeholder="agency@gmail.com"
                  />
                </div>

                <div className="email-config-field">
                  <label htmlFor="host">SMTP host</label>
                  <input
                    id="host"
                    name="host"
                    value={form.host}
                    onChange={onChange}
                    required
                    placeholder="smtp.gmail.com"
                  />
                </div>

                <div className="email-config-field">
                  <label htmlFor="port">SMTP port</label>
                  <input
                    id="port"
                    name="port"
                    type="number"
                    min="1"
                    max="65535"
                    value={form.port}
                    onChange={onChange}
                    required
                  />
                </div>

                <div className="email-config-field">
                  <label htmlFor="username">SMTP username</label>
                  <input
                    id="username"
                    name="username"
                    value={form.username}
                    onChange={onChange}
                    autoComplete="off"
                    placeholder="Usually the same as email address"
                  />
                </div>

                <div className="email-config-field">
                  <label htmlFor="password">App password</label>
                  <div className="email-config-password">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={onChange}
                      autoComplete="new-password"
                      placeholder={
                        hasPassword ? "Leave blank to keep saved password" : "16-character Gmail app password"
                      }
                    />
                    <button
                      type="button"
                      className="email-config-password__toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>
                  <span className="hint">
                    {hasPassword
                      ? "A password is already saved and encrypted."
                      : "Never use your normal Gmail password here."}
                  </span>
                </div>

                <div className="email-config-field email-config-field--full">
                  <label>Security</label>
                  <div className="email-config-security" role="group" aria-label="SMTP security">
                    {SECURITY_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`email-config-security__opt ${
                          form.secureMode === opt.value ? "is-active" : ""
                        }`}
                        onClick={() => setSecureMode(opt.value)}
                      >
                        <strong>{opt.title}</strong>
                        <span>{opt.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="email-config-field email-config-field--full">
                  <div className="email-config-toggle">
                    <div>
                      <strong>Enable this SMTP account</strong>
                      <p>When on, banker notification emails are sent from this mailbox.</p>
                    </div>
                    <label className="email-config-switch">
                      <input
                        id="enabled"
                        name="enabled"
                        type="checkbox"
                        checked={form.enabled}
                        onChange={onChange}
                      />
                      <span />
                    </label>
                  </div>
                </div>
              </div>

              <div className="email-config-actions">
                <button type="submit" className="email-config-btn email-config-btn--primary" disabled={saving}>
                  <FiSave aria-hidden />
                  {saving ? "Saving…" : "Save configuration"}
                </button>
              </div>
            </div>
          </form>

          <aside className="email-config-stack">
            <section className="email-config-card">
              <div className="email-config-card__head">
                <div>
                  <h2>Send test email</h2>
                  <p>Save settings first, then send a test to confirm login and delivery.</p>
                </div>
              </div>
              <div className="email-config-card__body">
                <div className="email-config-test-row">
                  <div className="email-config-field">
                    <label htmlFor="testTo">Recipient</label>
                    <input
                      id="testTo"
                      type="email"
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div className="email-config-actions">
                  <button
                    type="button"
                    className="email-config-btn email-config-btn--ghost"
                    disabled={!canTest}
                    onClick={handleTest}
                  >
                    <FiSend aria-hidden />
                    {testing ? "Sending…" : "Send test email"}
                  </button>
                </div>
                <p className="hint email-config-inline-hint">
                  Mail recipient inbox में आएगी (Inbox / Promotions / Spam), sender
                  inbox में नहीं। Sender Gmail → Sent में भी दिखेगी।
                </p>
                {!canTest && !testing ? (
                  <p className="hint email-config-inline-hint">
                    Save and enable SMTP, including an app password, before testing.
                  </p>
                ) : null}
              </div>
            </section>

            <section className="email-config-card">
              <div className="email-config-card__head">
                <div>
                  <h2>How banker email is sent</h2>
                  <p>WhatsApp and SMS are unchanged. Only the email sender uses this SMTP.</p>
                </div>
              </div>
              <div className="email-config-card__body">
                <ol className="email-config-flow">
                  <li>
                    <span className="email-config-flow__n">1</span>
                    <span>Confirmation → Email → banker email (notifyEmail).</span>
                  </li>
                  <li>
                    <span className="email-config-flow__n">2</span>
                    <span>Your saved SMTP account becomes the From address.</span>
                  </li>
                  <li>
                    <span className="email-config-flow__n">3</span>
                    <span>
                      Repo Intimation body plus Front, Side, Back, and Crane Bill attachments.
                    </span>
                  </li>
                </ol>
                <p className="hint email-config-lock-hint">
                  <FiLock aria-hidden />
                  Password is encrypted in the database and never returned to this page.
                </p>
              </div>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}
